import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Gem, Loader2 } from 'lucide-react';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { useAuth } from '@/contexts/AuthContext';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import LoginModal from '@/components/LoginModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import ShareToXButton from '@/components/ShareToXButton';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import { MegaTierFunnel } from '@/components/megatier/MegaTierFunnel';
import { MegaTierTable } from '@/components/megatier/MegaTierTable';
import { MegaTierRunButton } from '@/components/megatier/MegaTierRunButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ['dividend', 'all', 'non_dividend'];
const STAGES = ['elite', '3', '2', '1', 'all'];
const SS_KEY = 'megaTierView';

function initialView(search) {
  const params = new URLSearchParams(search);
  let saved = {};
  try { saved = JSON.parse(sessionStorage.getItem(SS_KEY) || '{}'); } catch { saved = {}; }
  const f = params.get('filter') || saved.filter;
  const s = params.get('stage') || saved.stage;
  return { filter: FILTERS.includes(f) ? f : 'dividend', stage: STAGES.includes(s) ? s : 'elite' };
}

export default function MegaTier() {
  const { isDark } = useMarketTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = (user?.role || '') === 'admin';
  const [{ filter, stage }, setView] = useState(() => initialView(location.search));
  const setFilter = (f) => setView((v) => ({ ...v, filter: f }));
  const setStage = (s) => setView((v) => ({ ...v, stage: s }));
  useEffect(() => {
    // Remember the switch + stage across Back navigation and reloads.
    try { sessionStorage.setItem(SS_KEY, JSON.stringify({ filter, stage })); } catch { /* ignore */ }
    const params = new URLSearchParams(location.search);
    if (params.get('filter') !== filter || params.get('stage') !== stage) {
      params.set('filter', filter);
      params.set('stage', stage);
      navigate({ search: `?${params.toString()}` }, { replace: true });
    }
  }, [filter, stage, location.search, navigate]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    // SEO head — mirrors FounderLedPage. Canonical is param-free so the
    // filter/stage variants collapse to one indexed URL.
    document.title = 'Mega Tier Undervalued Stocks — Quality + Graham + Piotroski Deep-Value Screen | TwylightCrow';
    const canonical = 'https://twylightcrow.com/mega-tier';
    const ogTitle = 'Mega Tier Undervalued Stocks — Financially Sound Quality Companies at Deep-Value Prices';
    const ogDesc = 'A weekly three-stage screen: top-20% Return-on-Capital + Earnings-Yield rank, at least 4 of 5 Benjamin Graham deep-value checks, then a Piotroski F-Score of 7+. Dividend and non-dividend stocks, with per-test pass/fail breakdowns.';
    const ogImage = 'https://twylightcrow.com/dividrip-icon.png?v=3';
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', ogDesc);
    setMeta('name', 'keywords', 'undervalued stocks, deep value screen, Piotroski F-Score, Benjamin Graham stocks, net-net stocks, earnings yield return on capital, value investing screener');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'TwylightCrow — DiviDrip');
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDesc);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDesc);
    setMeta('name', 'twitter:image', ogImage);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/mega-tier`, { params: { filter, stage }, withCredentials: true });
      setData(res.data);
    } catch { setData((d) => d || { items: [], counts: {} }); }
    finally { setLoading(false); }
  }, [filter, stage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.job?.in_progress) return undefined;
    const id = setInterval(() => load(true), 5000);
    return () => clearInterval(id);
  }, [data?.job?.in_progress, load]);
  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true }).then((r) => setWatchlist(r.data.map((p) => p.ticker))).catch(() => {});
  }, [user]);

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      else await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => (inList ? w.filter((t) => t !== ticker) : [...w, ticker]));
    } catch { /* ignore */ }
  };

  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    try {
      const d = await fetchStockWithFallback(ticker, { timeoutMs: 3500, onFresh: (fresh) => setSelectedStock(fresh) });
      setSelectedStock(d);
    } catch { setSelectedStock(null); }
    finally { setLoadingDetail(false); }
  };
  useReopenModalOnMount(handleStockClick);

  return (
    <div className="min-h-screen bg-background" data-testid="mega-tier-page">
      <div className="w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <BackOrHomeButton testid="mega-tier-back-or-home-btn" showEscHint={false} />
        <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" data-testid="mega-tier-title">
              <Gem className="w-7 h-7 text-amber-500" /> Mega Tier Undervalued Stocks
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="mega-tier-subtitle">
              Financially sound, high-quality companies trading at deep-value prices. Every operating company is
              ranked on <strong>Return on Capital</strong> (EBIT ÷ net PP&amp;E + working capital) and <strong>Earnings Yield</strong>{' '}
              (EBIT ÷ enterprise value); the top 20% combined must then pass at least four of five <strong>Graham deep-value checks</strong>{' '}
              and finally post a <strong>Piotroski F-Score of 7+</strong> to prove the bargain isn't a value trap.
              Financials, utilities, REITs, BDCs and funds are excluded.{' '}
              <Link to="/learn/graham-piotroski-deep-value" className="ht-inline-link" data-testid="mega-tier-guide-link">Read the Graham + Piotroski guide →</Link>
            </p>
          </div>
          {isAdmin && <MegaTierRunButton job={data?.job} meta={data?.meta} onStarted={() => load(true)} />}
        </div>
        <div className="mt-3" data-testid="mega-tier-share-row">
          <ShareToXButton
            variant="button"
            url="https://twylightcrow.com/api/share/og-page/mega-tier"
            text="Mega Tier Undervalued Stocks — financially sound quality companies at deep-value prices: top-20% quality + value rank, Graham checks, Piotroski F-Score 7+. Dividend and non-dividend:"
            hashtags={['DeepValue', 'ValueInvesting', 'Piotroski', 'BenjaminGraham']}
            ticker="mega-tier"
            label="Share on X"
          />
        </div>

        <div className="mt-4 flex justify-center" data-testid="mega-tier-dividend-switch">
          <DividendFilterSegmentedControl value={filter} isAdmin={isAdmin} instanceId="mega-tier" onChange={setFilter} />
        </div>

        <div className="mt-5">
          <MegaTierFunnel counts={data?.counts} stage={stage} onStage={setStage} />
        </div>

        <div className="mt-5 rounded-lg border border-border bg-card overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground" data-testid="mega-tier-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : (
            <MegaTierTable rows={data?.items || []} onSelect={handleStockClick} />
          )}
        </div>
        {!data?.meta && !loading && (
          <p className="text-xs text-muted-foreground mt-3" data-testid="mega-tier-no-run">
            The funnel hasn't been computed yet. Admins: use "Run now" above or the weekly cron dropdown (Mega Tier Undervalued Funnel).
          </p>
        )}
        <SiteDisclaimer />
      </div>

      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        user={user}
        onLoginRequired={() => setLoginOpen(true)}
        onPositionsChanged={() => window.dispatchEvent(new Event('portfolio-changed'))}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        onCompareClick={(t) => { if (t) { setCompareTicker(t); setCompareOpen(true); } }}
        initialTab="financial-health"
        initialChartPeriod={null}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
